import React, { useEffect, useState } from "react";
import { ChevronRight, LucideCoins, LucideWallet } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as walletActions from "src/redux/slices/walletSlice";
import Shimmer from "src/components/common/Shimmer";
import Button from "../Button";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

const WalletCard: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const handleNavigate = () => {
    // Event: WALLET_CARD_USE_COINS_CLICKED
    analytics.trackEvent(EVENT_NAMES.WALLET_CARD_USE_COINS_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      card_title: "Wallet",
      support_program_key: supportProgramKey,
    });

    // Navigate to wallet screen
    navigate(`/app/${supportProgramKey}/wallet`);
  };
  const { patient } = useAppSelector((state) => state.auth);
  const { walletAmount, coinsValue } = useAppSelector((state) => state.wallet);
  const [coins, setCoins] = useState<number>(0);

  const createWalletIdApi = () => {
    const payload = {
      contact_no: patient?.data?.contact_no,
    };
    new Promise((resolve, reject) => {
      dispatch(
        walletActions.createWalletIdRequest({
          payload,
          resolve,
          reject,
        })
      );
    }).then((res: any) => {
      if (Number(res?.code) === 1) {
        fetchWalletAmount();
      }
    });
  };

  const fetchWalletAmount = () => {
    new Promise((resolve, reject) => {
      dispatch(walletActions.fetchWalletAmountRequest({ resolve, reject }));
    }).then((res: any) => {
      let coins = Number(res?.data?.balance ?? 0) / 100;
      if (coinsValue) {
        coins = coins * coinsValue;
      }
      setCoins(coins);
    });
  };

  useEffect(() => {
    createWalletIdApi();
  }, []);

  useEffect(() => {
    fetchWalletAmount();
  }, []);

  return (
    <div className="relative mx-4 border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-[var(--app-primary)] to-[var(--app-primary-hover)]">
      <div className="flex flex-row items-center justify-between text-white">
        <div className="flex flex-row items-center gap-2">
          <LucideWallet className="w-4 h-4" />
          <span className="text-base font-medium">Wallet</span>
        </div>
        <Button
          onClick={handleNavigate}
          variant="contained"
          rounded="full"
          size="sm"
          color="white"
        >
          <span>Use Coins</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      <div className="border-2 border-dashed border-white rounded-md mt-4 p-4 flex flex-col items-center text-white justify-between">
        {walletAmount?.isLoading ? (
          <div className="flex items-center justify-center gap-2 text-3xl font-bold mb-3">
            <Shimmer width="150px" height="30px" />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-3xl font-bold mb-3">
            <LucideCoins />
            <div className="font-medium">{coins} Coins</div>
          </div>
        )}
        <h5 className="text-base text-center font-semibold">1 coin = ₹1</h5>
        <p className="text-sm text-center">
          Available Balance can be redeemed for consultations, lab tests
        </p>
      </div>

      {/* DESIGN DOTS */}
      <div className="absolute inset-0 pointer-events-none -z-10 opacity-50">
        <div className="absolute top-2 left-4 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-4 left-8 w-3 h-3 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-1 left-12 w-1.5 h-1.5 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-6 right-8 w-3 h-3 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-2 right-4 w-2.5 h-2.5 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-8 right-12 w-4 h-4 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-1/2 left-1/3 w-6 h-6 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-1/3 right-6 w-5 h-5 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-2/3 left-6 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute bottom-8 left-8 w-4 h-4 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute bottom-4 right-4 w-2.5 h-2.5 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute bottom-6 left-1/4 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute bottom-2 right-8 w-3 h-3 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-3 right-1/4 w-3.5 h-3.5 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-1/4 left-2 w-1 h-1 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute bottom-1/3 right-2 w-2 h-2 rounded-full bg-[var(--app-primary)]"></div>
        <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 rounded-full bg-[var(--app-primary)]"></div>
      </div>
    </div>
  );
};

export default WalletCard;
